import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import BottomSheet from '../components/mobile/BottomSheet'
import PrimaryButton from '../components/mobile/PrimaryButton'
import { KILO } from '../constants/KILO_THEME'
import type { BillingInterval } from '../lib/billingInterval'
import type { CheckoutPlan } from '../lib/planTier'
import { redirectToCheckout } from '../lib/stripeBilling'
import { setSubscriptionStatus } from '../lib/tier'
import { supabase } from '../lib/supabaseClient'

type LoadingChoice = 'free' | `${CheckoutPlan}:${BillingInterval}` | null

function BillingIntervalToggle({
  value,
  disabled,
  onChange,
}: {
  value: BillingInterval
  disabled: boolean
  onChange: (interval: BillingInterval) => void
}) {
  const { t } = useTranslation()

  return (
    <div className={`rounded-2xl border border-kilo-border bg-kilo-nested p-1`}>
      <div className="grid grid-cols-2 gap-1">
        {(['monthly', 'yearly'] as const).map((interval) => {
          const active = value === interval
          return (
            <button
              key={interval}
              type="button"
              disabled={disabled}
              onClick={() => onChange(interval)}
              className={`rounded-xl px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
                active ? KILO.pill.active : `${KILO.text.muted} hover:bg-kilo-card`
              }`}
            >
              {t(interval === 'monthly' ? 'paywall.monthly' : 'paywall.yearly')}
            </button>
          )
        })}
      </div>
      {value === 'yearly' ? (
        <p className="mt-2 text-center text-xs font-medium text-kilo-accent">{t('paywall.yearlySave')}</p>
      ) : null}
    </div>
  )
}

const Paywall = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly')
  const [loadingChoice, setLoadingChoice] = useState<LoadingChoice>(null)
  const [error, setError] = useState('')
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled'

  useEffect(() => {
    if (!checkoutCancelled) return
    setError(t('paywall.checkoutCancelled'))
  }, [checkoutCancelled, t])

  const proPriceKey = billingInterval === 'yearly' ? 'paywall.proPriceYearly' : 'paywall.proPriceMonthly'
  const proPlusPriceKey = billingInterval === 'yearly' ? 'paywall.proPlusPriceYearly' : 'paywall.proPlusPriceMonthly'
  const tryProKey = billingInterval === 'yearly' ? 'paywall.tryProYearly' : 'paywall.tryPro'
  const tryProPlusKey = billingInterval === 'yearly' ? 'paywall.tryProPlusYearly' : 'paywall.tryProPlus'

  const chooseFreeTier = async () => {
    setLoadingChoice('free')
    setError('')

    const { data: session } = await supabase.auth.getSession()
    const userId = session.session?.user?.id
    if (!userId) {
      navigate('/')
      return
    }

    const { error: tierError } = await setSubscriptionStatus(userId, 'free')
    if (tierError) {
      setError(tierError)
      setLoadingChoice(null)
      return
    }

    navigate('/app?tab=coach')
  }

  const startPaidTrial = async (plan: CheckoutPlan) => {
    const choice: LoadingChoice = `${plan}:${billingInterval}`
    setLoadingChoice(choice)
    setError('')

    const { data: session } = await supabase.auth.getSession()
    const userId = session.session?.user?.id
    if (!userId) {
      navigate('/')
      return
    }

    try {
      await supabase
        .from('profiles')
        .update({ paywall_dismissed_at: new Date().toISOString() })
        .eq('id', userId)

      await redirectToCheckout(plan, billingInterval)
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : t('paywall.checkoutError'))
      setLoadingChoice(null)
    }
  }

  const loading = loadingChoice !== null

  return (
    <div className={`min-h-screen ${KILO.bg.page}`}>
      <BottomSheet variant="dark" open onClose={() => void chooseFreeTier()} title={t('paywall.title')}>
        <div className="flex max-h-[75vh] flex-col gap-4 overflow-y-auto pb-4">
          <ul className={`space-y-2 text-sm ${KILO.text.muted}`}>
            <li>✅ {t('paywall.checklistProfile')}</li>
            <li>✅ {t('paywall.checklistPlan')}</li>
            <li>✅ {t('paywall.checklistMeals')}</li>
          </ul>

          <div className={`rounded-2xl border-2 border-kilo-line ${KILO.bg.nested} p-4`}>
            <p className={`text-sm font-semibold ${KILO.text.primary}`}>{t('paywall.freeTier')}</p>
            <ul className={`mt-2 space-y-1 text-sm ${KILO.text.muted}`}>
              <li>✅ {t('paywall.freeTraining')}</li>
              <li>✅ {t('paywall.freeMeals')}</li>
              <li>✅ {t('paywall.freeShopping')}</li>
              <li>✅ {t('paywall.freeProgress')}</li>
            </ul>
            <div className="mt-4">
              <PrimaryButton disabled={loading} variant="secondary" onClick={() => void chooseFreeTier()}>
                {loadingChoice === 'free' ? t('paywall.working') : t('paywall.startFree')}
              </PrimaryButton>
            </div>
          </div>

          <BillingIntervalToggle value={billingInterval} disabled={loading} onChange={setBillingInterval} />

          <div className={`rounded-2xl border border-kilo-border ${KILO.bg.card} p-4`}>
            <p className={`text-sm font-semibold ${KILO.text.primary}`}>{t('paywall.proTitle')}</p>
            <p className={`mt-1 text-sm font-semibold ${KILO.text.primary}`}>{t(proPriceKey)}</p>
            <p className={`mt-1 text-sm ${KILO.text.muted}`}>{t('paywall.proSubtitle')}</p>
            <ul className={`mt-2 space-y-1 text-xs ${KILO.text.muted}`}>
              <li>✅ {t('paywall.proWearables')}</li>
              <li>✅ {t('paywall.proPredictive')}</li>
              <li>✅ {t('paywall.proLive')}</li>
            </ul>
            <p className={`mt-2 text-xs ${KILO.text.soft}`}>{t('paywall.premiumTrialNote')}</p>
            <div className="mt-4">
              <PrimaryButton disabled={loading} onClick={() => void startPaidTrial('pro')}>
                {loadingChoice === `pro:${billingInterval}` ? t('paywall.startingCheckout') : t(tryProKey)}
              </PrimaryButton>
            </div>
          </div>

          <div className={`rounded-2xl border-2 border-kilo-accent/40 ${KILO.bg.card} p-4 ring-1 ring-kilo-accent/20`}>
            <p className={`text-sm font-semibold text-kilo-accent`}>{t('paywall.proPlusTitle')}</p>
            <p className={`mt-1 text-sm font-semibold ${KILO.text.primary}`}>{t(proPlusPriceKey)}</p>
            <p className={`mt-1 text-sm ${KILO.text.muted}`}>{t('paywall.proPlusSubtitle')}</p>
            <ul className={`mt-2 space-y-1 text-xs ${KILO.text.muted}`}>
              <li>✅ {t('paywall.proPlusCalendar')}</li>
              <li>✅ {t('paywall.proPlusShopping')}</li>
              <li>✅ {t('paywall.proPlusIncludesPro')}</li>
            </ul>
            <p className={`mt-2 text-xs ${KILO.text.soft}`}>{t('paywall.premiumTrialNote')}</p>
            <div className="mt-4">
              <PrimaryButton disabled={loading} onClick={() => void startPaidTrial('pro_plus')}>
                {loadingChoice === `pro_plus:${billingInterval}` ? t('paywall.startingCheckout') : t(tryProPlusKey)}
              </PrimaryButton>
            </div>
          </div>

          <button
            type="button"
            disabled={loading}
            className={`h-12 text-sm ${KILO.text.soft}`}
            onClick={() => void chooseFreeTier()}
          >
            {t('paywall.skip')}
          </button>

          {error ? <p className="text-sm text-kilo-danger">{error}</p> : null}
        </div>
      </BottomSheet>
    </div>
  )
}

export default Paywall
